import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import MatchLogger from '../components/matches/MatchLogger.jsx';
import Loading from '../components/common/Loading.jsx';
import ErrorMessage from '../components/common/ErrorMessage.jsx';
import { submitMatchResults, getMatchDetails } from '../services/matches-api.js';
import { getTeamMembers } from '../services/team-members-api.js';
import './LogMatchPage.css';

export default function LogMatchPage() {
  const { matchId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [match, setMatch] = useState(null);
  const [team1Players, setTeam1Players] = useState([]);
  const [team2Players, setTeam2Players] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchMatchData();
  }, [matchId]);

  const fetchMatchData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Try API first, fall back to navigation state
      let matchData = location.state?.match;
      let players1 = [];
      let players2 = [];

      try {
        const details = await getMatchDetails(matchId);
        matchData = details.match;
        players1 = (details.team1Players || []).map(p => ({
          playerId: p.playerId,
          name: `${p.firstName} ${p.lastName}`,
        }));
        players2 = (details.team2Players || []).map(p => ({
          playerId: p.playerId,
          name: `${p.firstName} ${p.lastName}`,
        }));
      } catch (err) {
        console.error('Failed to fetch match details from API:', err);
        // API failed — use navigation state if available
        if (!matchData) {
          setError('Match data not available. Please navigate from the Captain Dashboard.');
          setLoading(false);
          return;
        }
        // Fetch rosters via team members endpoint as fallback
        try {
          const [team1Res, team2Res] = await Promise.all([
            getTeamMembers(matchData.team1Id),
            getTeamMembers(matchData.team2Id),
          ]);
          players1 = (team1Res.members || []).map(m => ({ playerId: m.playerId, name: m.name }));
          players2 = (team2Res.members || []).map(m => ({ playerId: m.playerId, name: m.name }));
        } catch (rosterErr) {
          console.error('Failed to fetch team rosters:', rosterErr);
        }
      }

      setMatch({
        matchId: matchData.matchId || matchId,
        team1Id: matchData.team1Id,
        team2Id: matchData.team2Id,
        team1Name: matchData.team1Name,
        team2Name: matchData.team2Name,
        scheduledDate: matchData.scheduledDate,
        scheduledTime: matchData.scheduledTime,
      });

      setTeam1Players(players1);
      setTeam2Players(players2);
    } catch (err) {
      setError(err.message || 'Failed to load match data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (results) => {
    try {
      const response = await submitMatchResults(matchId, results);
      
      // Show success message based on response status
      alert(response.message || 'Match results submitted successfully');
      
      // Navigate back to dashboard
      navigate('/captain/dashboard');
    } catch (err) {
      throw err; // Let MatchLogger handle the error
    }
  };

  const handleCancel = () => {
    navigate('/captain/dashboard');
  };

  if (loading) {
    return (
      <div className="log-match-page">
        <Loading />
      </div>
    );
  }

  if (error) {
    return (
      <div className="log-match-page">
        <ErrorMessage message={error} onRetry={fetchMatchData} />
      </div>
    );
  }

  return (
    <div className="log-match-page">
      <MatchLogger
        match={match}
        team1Players={team1Players}
        team2Players={team2Players}
        gamesPerMatch={7}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
      />
    </div>
  );
}
